import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Icon } from "@/components/Icon";
import iconMark from "@/assets/pnrbet-icon.png";

/* CasinoHero — the rotating spotlight at the TOP of the mobile casino lobby.
   It rotates through a MIX of games (not only Originals): the celeb Featured
   titles FIRST, then a few PRYMEBET Originals, then a couple top provider games.
   Full-bleed art + dark left scrim + eyebrow + big name + tagline + green Play CTA.
   Auto-advances every ~5s (paused while the user interacts or the tab is hidden),
   is swipeable, has dot indicators, and respects prefers-reduced-motion (no auto
   rotation). Tapping Play/the slide calls onPlay(g) — MobileCasino routes native
   vs. provider launch. Pool is pre-built by MobileCasino and passed in. */

const AUTO_MS = 5000;

// Eyebrow label: provider studio for provider games, else "PRYMEBET ORIGINAL".
function eyebrow(g) {
  if (g.provider_game) return (g.providerLabel || "PROVIDER").toUpperCase();
  return "PRYMEBET ORIGINAL";
}
// Short tagline — honest, derived from what we know (live / original / provider).
function tagline(g) {
  if (g.live) return "Live dealer · streaming now";
  if (g.provider_game) return "Featured slot · spin to win";
  return "In-house game · play instantly";
}

export default function CasinoHero({ games = [], onPlay }) {
  const slides = useMemo(() => (Array.isArray(games) ? games.slice(0, 6) : []), [games]);
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const trackRef = useRef(null);
  const touch = useRef({ x: 0, dx: 0, active: false });

  // Reduced-motion: no auto-advance (checked once; harmless if matchMedia absent).
  const reducedMotion = useMemo(() => {
    try { return window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches; }
    catch { return false; }
  }, []);

  // Clamp index if the pool shrinks (e.g. data reload).
  useEffect(() => { if (idx > slides.length - 1) setIdx(0); }, [slides.length, idx]);

  const go = useCallback((n) => {
    const len = slides.length;
    if (!len) return;
    setIdx(((n % len) + len) % len);
  }, [slides.length]);

  // Auto-advance; paused on interaction, when hidden, under reduced-motion, or <2 slides.
  useEffect(() => {
    if (reducedMotion || paused || slides.length < 2) return;
    if (typeof document !== "undefined" && document.hidden) return;
    const t = setTimeout(() => go(idx + 1), AUTO_MS);
    return () => clearTimeout(t);
  }, [idx, paused, slides.length, reducedMotion, go]);

  // Pause auto-rotate while the tab is hidden; resume when visible.
  useEffect(() => {
    const onVis = () => setPaused(document.hidden);
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []);

  if (!slides.length) return null;

  // Swipe handling (touch). A >40px horizontal drag advances/retreats.
  const onTouchStart = (e) => { touch.current = { x: e.touches[0].clientX, dx: 0, active: true }; setPaused(true); };
  const onTouchMove = (e) => { if (touch.current.active) touch.current.dx = e.touches[0].clientX - touch.current.x; };
  const onTouchEnd = () => {
    const { dx, active } = touch.current;
    touch.current.active = false;
    if (active && Math.abs(dx) > 40) go(idx + (dx < 0 ? 1 : -1));
    // resume auto-rotate shortly after the gesture settles
    setTimeout(() => setPaused(false), 600);
  };

  const g = slides[idx];

  return (
    <div
      style={{ padding: "0 16px" }}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div
        ref={trackRef}
        role="group"
        aria-roledescription="carousel"
        aria-label="Featured games"
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        style={{
          position: "relative", width: "100%", height: 190, borderRadius: 22, overflow: "hidden",
          background: "#141417", boxShadow: "0 14px 30px rgba(0,0,0,.45), inset 0 0 0 1px rgba(255,255,255,.06)",
        }}
      >
        {/* Full-bleed art */}
        {g.img
          ? <img key={g.id} src={g.img} alt={g.name} loading="eager" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }} />
          : <span style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}><Icon name="casino" size={40} style={{ color: "#3F3F46" }} /></span>}

        {/* Dark left scrim so text stays legible over any art */}
        <span style={{ position: "absolute", inset: 0, background: "linear-gradient(90deg, rgba(5,5,6,.92) 0%, rgba(5,5,6,.72) 34%, rgba(5,5,6,.12) 72%, rgba(5,5,6,0) 100%)" }} />

        {/* Content */}
        <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", justifyContent: "flex-end", gap: 8, padding: "16px 16px 40px" }}>
          <span style={{ font: "800 10px 'Outfit'", letterSpacing: ".16em", color: "#00D632", textTransform: "uppercase", display: "inline-flex", alignItems: "center", gap: 5 }}>
            {/* PRYMEBET Originals carry the little Pryme logo mark (matches desktop
                FeaturedComposition); provider games show their studio name only. */}
            {!g.provider_game && !g.live && (
              <img src={iconMark} alt="" style={{ width: 13, height: 13, borderRadius: 3, objectFit: "contain", flex: "none" }} />
            )}
            {eyebrow(g)}
          </span>
          <span style={{ font: "800 26px/1.05 'Manrope'", letterSpacing: "-.02em", color: "#fff", maxWidth: "80%", textShadow: "0 2px 12px rgba(0,0,0,.5)" }}>{g.name}</span>
          <span style={{ font: "500 12.5px 'Outfit'", color: "#D4D4D8", maxWidth: "72%" }}>{tagline(g)}</span>
          <button
            type="button" onClick={() => onPlay?.(g)} aria-label={`Play ${g.name}`}
            style={{ marginTop: 4, alignSelf: "flex-start", height: 38, padding: "0 16px", borderRadius: 12, display: "inline-flex", alignItems: "center", gap: 7, border: 0, cursor: "pointer",
              background: "#00D632", color: "#0A0A0A", font: "800 14px 'Manrope'", boxShadow: "inset 0 1px 0 rgba(255,255,255,.3), 0 6px 16px rgba(0,214,50,.3)" }}
          >
            <Icon name="play" size={14} style={{ color: "#0A0A0A" }} /> Play
          </button>
        </div>

        {/* Dot indicators */}
        {slides.length > 1 && (
          <div style={{ position: "absolute", left: 0, right: 0, bottom: 12, display: "flex", justifyContent: "center", gap: 6 }}>
            {slides.map((s, i) => (
              <button
                key={s.id} type="button" aria-label={`Go to slide ${i + 1}`} aria-current={i === idx}
                onClick={() => { setPaused(true); go(i); setTimeout(() => setPaused(false), 600); }}
                style={{ width: i === idx ? 18 : 6, height: 6, borderRadius: 999, border: 0, cursor: "pointer", padding: 0,
                  background: i === idx ? "#00D632" : "rgba(255,255,255,.4)", transition: "width .2s ease" }}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
